import React, { useCallback } from 'react'
import { GetServerSideProps } from 'next'
import { createCombinedServerSideProps } from '../src/utils/server/page-props'
import { useTranslation } from 'next-i18next'
import { getLayout } from '../src/layouts/MainLayout'
import { ConsumerPage } from '../src/model'
import { OrdersList, NoOrders, Head } from '../src/component'
import { useGetOrderTransactionsQuery, useHasOrderTransactions } from '../src/state'
import { defaultOrdersParams } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { LoadingListPage } from '../src/component/LoadingListPage'
import { TrackPageViewed } from '../src/integrations/amplitude'
import { ExtendedPanel } from '../src/component/Panel'
import { Panel } from '@afterpaytouch/core'

const I18N_NAMESPACE = ['common', 'orders', 'index', 'payments', 'ach']

const useOrdersPageEffects = (): {
  hasOrders: boolean
  isFetching: boolean
  isLoading: boolean
  isError: boolean
  refetchOrdersPage: () => void
} => {
  const {
    isFetching: isGetOrderTransactionsQueryFetching,
    isLoading: isGetOrderTransactionsQueryLoading,
    isError: isGetOrderTransactionsQueryError,
    refetch: refetchGetOrderTransactionsQuery,
  } = useGetOrderTransactionsQuery(defaultOrdersParams)

  const refetchOrdersPage = useCallback(() => {
    if (isGetOrderTransactionsQueryError) {
      refetchGetOrderTransactionsQuery()
    }
  }, [isGetOrderTransactionsQueryError, refetchGetOrderTransactionsQuery])

  return {
    hasOrders: useHasOrderTransactions(),
    isFetching: isGetOrderTransactionsQueryFetching,
    isLoading: isGetOrderTransactionsQueryLoading,
    isError: isGetOrderTransactionsQueryError,
    refetchOrdersPage,
  }
}

const OrdersPage: ConsumerPage = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { hasOrders, isLoading, isError, refetchOrdersPage } = useOrdersPageEffects()

  const renderLoadingList = (): JSX.Element => (
    <LoadingListPage tableCellHeaders={Array.from(Array(6).keys()).map((n) => t(`orders:orderList:tableHeadings:${n}`))} tableCellWidth={[240, 0, 120]} />
  )

  const pageTitle = `${t('orders:orders')} | ${t('common:brandName')}`

  return (
    <>
      <Head title={pageTitle} />
      {isError ? (
        <Panel>
          <Panel.Content>
            <ExtendedPanel.Error onClick={refetchOrdersPage} />
          </Panel.Content>
        </Panel>
      ) : isLoading ? (
        renderLoadingList()
      ) : hasOrders ? (
        <OrdersList />
      ) : (
        <NoOrders />
      )}
      <TrackPageViewed />
    </>
  )
}

OrdersPage.getLayout = getLayout()

export const getServerSideProps: GetServerSideProps = createCombinedServerSideProps(I18N_NAMESPACE)

export default OrdersPage
